// i18n 6amico.com — lingue ufficiali UE + fallback IT

/**
 * Lingua di default del network (Home, UI).
 * Priorità assoluta: italiano. Browser / locale ambigui → sempre "it".
 * Solo la bandierina (scelta esplicita) attiva en/fr/de/….
 */
export const DEFAULT_LANG = "it";

/** Lookup stile i18next: t("hero.title") + accesso oggetto t.hero.title */
export function makeT(dict) {
  const resolve = (path, vars) => {
    if (typeof path !== "string" || !path.trim()) return "";
    const parts = path.split(".");
    let cur = dict;
    for (const p of parts) {
      if (cur == null || typeof cur !== "object") return "";
      cur = cur[p];
    }
    let out = typeof cur === "string" ? cur : cur == null ? "" : String(cur);
    if (vars && typeof vars === "object" && out) {
      for (const [k, v] of Object.entries(vars)) {
        out = out.split(`{${k}}`).join(String(v ?? ""));
      }
    }
    return out;
  };
  const fn = (path, vars) => resolve(path, vars);
  return new Proxy(fn, {
    get(target, prop, receiver) {
      if (typeof prop === "symbol") return Reflect.get(target, prop, receiver);
      // Evita che React/thenable tratti t come Promise
      if (prop === "then") return undefined;
      // Chiavi del dizionario hanno priorità su proprietà Function (name, length, …)
      if (dict != null && Object.prototype.hasOwnProperty.call(dict, prop)) {
        return dict[prop];
      }
      if (prop === "length" || prop === "name" || prop === "prototype" || prop === "apply" || prop === "call" || prop === "bind") {
        return Reflect.get(target, prop, receiver);
      }
      return dict?.[prop];
    },
  });
}

/** Emoji bandiera leggibili (header / switcher compatto) */
export const LANG_EMOJI = {
  it: "🇮🇹",
  en: "🇬🇧",
  fr: "🇫🇷",
  de: "🇩🇪",
  es: "🇪🇸",
  pt: "🇵🇹",
  nl: "🇳🇱",
  pl: "🇵🇱",
  hr: "🇭🇷",
  el: "🇬🇷",
  ro: "🇷🇴",
  cs: "🇨🇿",
  sk: "🇸🇰",
  hu: "🇭🇺",
  bg: "🇧🇬",
  da: "🇩🇰",
  sv: "🇸🇪",
  fi: "🇫🇮",
  et: "🇪🇪",
  lv: "🇱🇻",
  lt: "🇱🇹",
  sl: "🇸🇮",
  mt: "🇲🇹",
  ga: "🇮🇪",
};

/** Menu header: lingue principali (niente lista infinita) */
export const PRIMARY_LANGUAGES = [
  { code: "it", label: "Italiano", emoji: LANG_EMOJI.it },
  { code: "en", label: "English", emoji: LANG_EMOJI.en },
  { code: "fr", label: "Français", emoji: LANG_EMOJI.fr },
  { code: "de", label: "Deutsch", emoji: LANG_EMOJI.de },
  { code: "es", label: "Español", emoji: LANG_EMOJI.es },
  { code: "pt", label: "Português", emoji: LANG_EMOJI.pt },
];

export const LANGUAGES = [
  ...PRIMARY_LANGUAGES.map(({ code, label }) => ({ code, label, flag: code.toUpperCase(), emoji: LANG_EMOJI[code] })),
  { code: "nl", label: "Nederlands", flag: "NL", emoji: LANG_EMOJI.nl },
  { code: "pl", label: "Polski", flag: "PL", emoji: LANG_EMOJI.pl },
  { code: "hr", label: "Hrvatski", flag: "HR", emoji: LANG_EMOJI.hr },
  { code: "el", label: "Ελληνικά", flag: "GR", emoji: LANG_EMOJI.el },
  { code: "ro", label: "Română", flag: "RO", emoji: LANG_EMOJI.ro },
  { code: "cs", label: "Čeština", flag: "CZ", emoji: LANG_EMOJI.cs },
  { code: "sk", label: "Slovenčina", flag: "SK", emoji: LANG_EMOJI.sk },
  { code: "hu", label: "Magyar", flag: "HU", emoji: LANG_EMOJI.hu },
  { code: "bg", label: "Български", flag: "BG", emoji: LANG_EMOJI.bg },
  { code: "da", label: "Dansk", flag: "DK", emoji: LANG_EMOJI.da },
  { code: "sv", label: "Svenska", flag: "SE", emoji: LANG_EMOJI.sv },
  { code: "fi", label: "Suomi", flag: "FI", emoji: LANG_EMOJI.fi },
  { code: "et", label: "Eesti", flag: "EE", emoji: LANG_EMOJI.et },
  { code: "lv", label: "Latviešu", flag: "LV", emoji: LANG_EMOJI.lv },
  { code: "lt", label: "Lietuvių", flag: "LT", emoji: LANG_EMOJI.lt },
  { code: "sl", label: "Slovenščina", flag: "SI", emoji: LANG_EMOJI.sl },
  { code: "mt", label: "Malti", flag: "MT", emoji: LANG_EMOJI.mt },
  { code: "ga", label: "Gaeilge", flag: "IE", emoji: LANG_EMOJI.ga },
];

const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

export function isSupportedLang(code) {
  return SUPPORTED.has((code || "it").split("-")[0].toLowerCase());
}

function deepMerge(base, patch) {
  if (!patch) return base;
  const out = { ...base };
  for (const key of Object.keys(patch)) {
    const pv = patch[key];
    const bv = base[key];
    if (pv && typeof pv === "object" && !Array.isArray(pv) && bv && typeof bv === "object" && !Array.isArray(bv)) {
      out[key] = deepMerge(bv, pv);
    } else {
      out[key] = pv;
    }
  }
  return out;
}

const GDPR_IT = {
  title: "Privacy e dati personali",
  lead: "Consenso granulare, portabilità e cancellazione (Reg. UE 2016/679).",
  policy_link: "Informativa completa →",
  consents_title: "Consenso al trattamento",
  essential: "Essenziali (servizio, sicurezza)",
  functional: "Funzionali (preferenze, lingua)",
  analytics: "Statistiche anonime",
  marketing: "Comunicazioni promozionali",
  third_party_maps: "Mappe e servizi di terze parti",
  community_publication: "Pubblicazione su Piazze Social",
  download: "Scarica i miei dati (JSON)",
  downloaded: "Download avviato",
  saved: "Preferenze salvate",
  erase_title: "Cancellazione account",
  erase_lead: "Eliminazione definitiva di profilo, post e punti (Art. 17).",
  erase_btn: "Elimina account",
  erase_confirm: "Conferma cancellazione",
  confirm_placeholder: "Digita ELIMINA per confermare",
  password: "Password",
  cancel: "Annulla",
  error: "Operazione non riuscita",
  language: "Lingua dell'interfaccia",
};

const NARRATION_IT = {
  listen: "Ascolta",
  pause: "Pausa",
  preparing: "Preparo la voce…",
  home_title: "Ascolta il testo storico",
  home_hint: "Presentazione in voce calda — stesso sistema dei monumenti.",
  home_storia_hint: "Legge il testo storico del borgo qui sotto (come nelle schede comune), non le foto del carosello.",
  vocal_label: "Narrazione vocale",
  fallback_notice: "Uso la voce del dispositivo: il servizio vocale del server non ha risposto.",
  server_notice: "Voce narrativa femminile 6amico",
  unavailable: "Narrazione non disponibile.",
  interrupted: "Narrazione interrotta. Riprova tra poco.",
};

const NARRATION_EN = {
  listen: "Listen",
  pause: "Pause",
  preparing: "Preparing voice…",
  home_title: "Listen to the home page",
  home_hint: "Warm voice presentation — same system as monument pages.",
  home_storia_hint: "Reads the mission and historical platform text below — not the photo carousel.",
  vocal_label: "Voice narration",
  fallback_notice: "Using your device voice: the server audio service did not respond.",
  server_notice: "6amico female narrative voice",
  unavailable: "Narration not available.",
  interrupted: "Narration stopped. Try again shortly.",
};

const GDPR_EN = {
  title: "Privacy & personal data",
  lead: "Granular consent, portability and erasure (EU GDPR).",
  policy_link: "Full privacy notice →",
  consents_title: "Processing consent",
  essential: "Essential (service, security)",
  functional: "Functional (preferences, language)",
  analytics: "Anonymous analytics",
  marketing: "Promotional communications",
  third_party_maps: "Third-party maps & services",
  community_publication: "Social Squares publication",
  download: "Download my data (JSON)",
  downloaded: "Download started",
  saved: "Preferences saved",
  erase_title: "Delete account",
  erase_lead: "Permanent deletion of profile, posts and points (Art. 17).",
  erase_btn: "Delete account",
  erase_confirm: "Confirm deletion",
  confirm_placeholder: "Type DELETE to confirm",
  cancel: "Cancel",
  error: "Operation failed",
  language: "Interface language",
};

export const translations = {
  it: {
    narration: NARRATION_IT,
    nav: {
      home: "Home",
      villages: "Borghi",
      qr: "Guide QR",
      piazze: "Piazza Social",
      merchants: "Vetrina",
      twinnings: "Gemellaggi",
      explore: "Esplora Borghi",
      events: "Eventi & Sagre",
      network: "Network",
      world: "Mondo",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "Dal 2013 · Guide intelligenti per i borghi",
      title: "Ogni pietra ha una storia. Scoprila.",
      subtitle: "Dai borghi d'Italia alle città del mondo, la memoria dei luoghi è a portata di clic.",
      sub: "Dai borghi d'Italia alle città del mondo, la memoria dei luoghi è a portata di clic.",
      cta_primary: "Scopri i Borghi",
      cta_explore: "Esplora",
      cta_secondary: "Scansiona un QR",
      qr_manual_toggle: "Hai un codice QR? Inseriscilo",
    },
    sections: {
      mission: "La nostra missione",
      mission_lead: "Restituiamo memoria e storie dei borghi a chi li vive e a chi li visita.",
      villages: "I Borghi",
      villages_sub: "Territori da esplorare, a partire dall'Abruzzo",
      qr: "Guide intelligenti",
      qr_sub: "Scansiona un QR sul territorio e apri la guida del luogo",
      piazze: "La piazza digitale",
      piazze_sub: "Foto, eventi e racconti dalla community dei borghi",
      merchants: "Turismo e territorio",
      merchants_sub: "Prodotti tipici e botteghe locali in vetrina",
      twinnings: "Gemellaggi",
      twinnings_sub: "Borghi italiani collegati alle città amiche nel mondo",
      chat: "Parla con 6amico",
      chat_sub: "Chiedi consigli su borghi, storie e prodotti tipici",
    },
    pages: {
      piazze: {
        kicker: "Network del Merito · Italia",
        title: "Piazza Social",
        lead: "Il feed condiviso di tutti i borghi del Network. Ogni post è un ricordo, una notizia o una segnalazione fatta dai cittadini. Reagisci con il Campanile, commenta, condividi una cartolina con la tua famiglia.",
        feed_aria: "Feed Piazza Social",
      },
      vetrina: {
        kicker: "MARKET · TERRITORIO",
        search_placeholder: "Cerca un comune… es. Campli, Castiglione",
        featured: "Borghi in evidenza",
        merchants_all: "Commercianti del network",
        merchants_for: "Vetrina · {name}",
        empty: "Nessun risultato per questo comune. Prova un altro borgo dalla ricerca.",
        filter_label: "Filtra",
        remove_filter: "Rimuovi",
      },
      eventi: {
        kicker: "CALENDARIO · TERRITORIO",
        title: "Eventi & Sagre",
        lead: "Solo date future, senza doppioni. Eventi pubblicati in archivio da Pro Loco e Comuni del network — si aggiorna quando le istituzioni caricano nuove date.",
        suggest: "Suggerisci un evento",
        loading: "Caricamento...",
        empty_title: "Nessun evento in arrivo",
        empty_lead: "Le Pro Loco e i Comuni stanno per pubblicare i prossimi eventi.",
        cat_all: "Tutti",
        cat_sagra: "Sagre",
        cat_festa: "Feste",
        cat_manifestazione: "Manifestazioni",
        cat_concerto: "Concerti",
        cat_mostra: "Mostre",
        cat_religioso: "Religiosi",
        cat_sportivo: "Sportivi",
      },
    },
    ui: {
      read_more: "Approfondisci",
      discover: "Scopri",
      visit: "Visita",
      by: "a cura di",
      year: "Anno",
      code: "Codice QR",
      scan_cta: "Inserisci codice QR (es. CAMPLI-001)",
      scan_button: "Visualizza Guida",
      contact: "Contatta",
      specialty: "Specialità",
      featured: "In evidenza",
      since: "Gemellato dal",
      country: "Paese",
      post: "Post",
      event: "Evento",
      news: "Notizia",
      new_post: "Pubblica qualcosa…",
      publish: "Pubblica",
      your_name: "Il tuo nome",
      choose_village: "Scegli borgo",
      ask_placeholder: "Chiedi allo Storico…",
      send: "Invia",
      open_chat: "Apri chat",
      close: "Chiudi",
      view_all: "Vedi tutto",
      show_translation: "Visualizza traduzione",
      footer_tagline: "6amico è un social network italiano dedicato alla scoperta e alla valorizzazione dei borghi, delle frazioni, degli eventi e dei prodotti tipici dei territori.",
      back_home: "Torna alla Home",
      landmarks: "Luoghi iconici",
      province: "Provincia",
      founded: "Fondato",
      population: "Abitanti",
      loading: "Caricamento…",
      not_found: "Contenuto non trovato",
      frazioni: "Frazioni",
      monuments: "Monumenti",
      qr_download: "Scarica QR Code",
      qr_print: "Stampa il QR per installarlo sul territorio",
      admin: "Amministrazione",
      admin_login: "Accedi al pannello",
      password: "Password",
      logout: "Esci",
      save: "Salva", saving: "Salvo…", saved: "Salvato",
      ai_fill: "Genera con AI", generating: "Generazione…",
      add_monument: "Aggiungi monumento", delete: "Elimina",
      video: "Video", paste_youtube: "Incolla URL YouTube/Vimeo",
      gallery: "Galleria", add_image_url: "URL immagine",
      add: "Aggiungi", edit: "Modifica",
      no_frazioni: "Nessuna frazione disponibile", explore_frazione: "Esplora",
      language: "Lingua",
      tabs: {
        storia: "Storia", storia_md: "Storia & info",
        frazioni: "Frazioni", frazioni_world: "Comuni",
        percorsi: "Percorsi", percorsi_md: "Percorsi e sentieri",
        mangiare: "Mangiare", mangiare_md: "Dove mangiare",
        dormire: "Dormire", dormire_md: "Dove dormire",
        prodotti: "Prodotti", prodotti_md: "Prodotti del comune", prodotti_world: "Specialità", prodotti_world_md: "Prodotti & specialità locali",
        luoghi: "Luoghi", luoghi_md: "Luoghi & cultura",
        proloco: "Pro Loco", proloco_md: "Pro Loco & eventi", proloco_world: "Eventi", proloco_world_md: "Eventi & istituzioni",
        altro: "Altro", movida: "Movida", movida_md: "Movida e notte", sub: "Sub", sub_md: "Sub e diving", barche: "Barche",
        luoghi_cosa_vedere: "Cosa vedere", luoghi_chiese: "Chiese", luoghi_musei: "Musei", luoghi_natura: "Natura",
        culto: "Culto", culto_title: "Luoghi di culto", monumenti: "Monumenti · Cosa vedere",
        chiama: "Chiama", sito: "Sito", indicazioni: "Indicazioni",
        link_istituzionale: "Link istituzionale", link_istituzionale_cultura: "Link istituzionale · cultura", video_istituzionale: "Video · link istituzionale",
        utility: "Utility", sismica: "Sismica", meteo: "Meteo",
        installa_app: "Installa App", installa_title: "Installa 6amico su iPhone",
        installa_intro: "Per avere 6amico a schermo intero come una vera applicazione sul tuo iPhone:",
        installa_s1: "Tocca l'icona Condividi in basso al centro in Safari (il quadrato con la freccia in su).",
        installa_s2: "Scorri il menù e tocca \"Aggiungi alla schermata Home\".",
        installa_s3: "Conferma con \"Aggiungi\" in alto a destra.",
        installa_done: "Ho capito",
        installa_hint_desktop: "Per installare l'app, clicca sull'icona con lo schermo o il '+' nella barra degli indirizzi del tuo browser.",
      },
    },
    footer: {
      blurb: "6amico valorizza i borghi italiani: mappe, storie, eventi e prodotti tipici in una sola community.",
      trust: "Privacy · GDPR",
      nav_title: "Navigazione",
      community_title: "Community",
      discover_title: "Scopri",
      welcome_tour: "Tour di benvenuto",
      how_it_works: "Come funziona",
      manifesto: "Manifesto",
      generation: "Generazione 6AMICO",
      census: "Censisci un luogo",
      request_access: "Richiedi un accesso",
      map: "Mappa dei borghi",
      comuni: "Comuni amici",
      vetrina: "Vetrina tipica",
      institutional: "Area istituzionale",
      institutional_lead: "Per comuni, Pro Loco e partner",
      open: "Apri",
      close: "Chiudi",
    },
    gdpr: GDPR_IT,
  },
  en: {
    narration: NARRATION_EN,
    nav: {
      home: "Home",
      villages: "Villages",
      qr: "QR Guides",
      piazze: "Social Squares",
      merchants: "Marketplace",
      twinnings: "Twinnings",
      explore: "Explore Villages",
      events: "Events & Festivals",
      network: "Network",
      world: "World",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · YOUR DIGITAL CICERONE ACROSS ITALY",
      title: "Every stone has a story. Discover it.",
      subtitle: "From Italy's villages to the cities of the world, the memory of places is one click away.",
      sub: "From Italy's villages to the cities of the world, the memory of places is one click away.",
      cta_primary: "Discover the Villages",
      cta_explore: "Explore",
      cta_secondary: "Scan a QR",
      qr_manual_toggle: "Have a code (e.g. CAMPLI-001)? Enter it here",
    },
    sections: {
      mission: "Our Mission",
      mission_lead: "We've retrieved thousands of historical texts and images from our archives to return a unique cultural heritage to citizens and travelers.",
      villages: "The Villages",
      villages_sub: "Four gateways into Abruzzo's memory",
      qr: "Smart Tourism",
      qr_sub: "Scan a QR Code on-site to unlock guides curated by historians",
      piazze: "Social Squares",
      piazze_sub: "Geolocated feed: photos, events and stories from the villages",
      merchants: "Local Economy",
      merchants_sub: "A showcase for merchants and producers — the soul of the territory",
      twinnings: "Sister cities",
      twinnings_sub: "Italian villages linked to partner cities worldwide",
      chat: "Talk to 6amico",
      chat_sub: "Ask anything about Abruzzo's history",
    },
    pages: {
      piazze: {
        kicker: "Merit Network · Italy",
        title: "Social Square",
        lead: "The shared feed of every village in the Network. Each post is a memory, a news item or a tip from citizens. React with the Bell Tower, comment, share a postcard with your family.",
        feed_aria: "Social Square feed",
      },
      vetrina: {
        kicker: "MARKET · TERRITORY",
        search_placeholder: "Search a town… e.g. Campli, Castiglione",
        featured: "Featured villages",
        merchants_all: "Network merchants",
        merchants_for: "Showcase · {name}",
        empty: "No results for this town. Try another village from search.",
        filter_label: "Filter",
        remove_filter: "Remove",
      },
      eventi: {
        kicker: "CALENDAR · TERRITORY",
        title: "Events & Festivals",
        lead: "Upcoming dates only, duplicates filtered. Events published in the archive by Pro Loco and network municipalities — updated when institutions add new dates.",
        suggest: "Suggest an event",
        loading: "Loading...",
        empty_title: "No upcoming events",
        empty_lead: "Pro Loco and municipalities will publish the next events soon.",
        cat_all: "All",
        cat_sagra: "Food festivals",
        cat_festa: "Feasts",
        cat_manifestazione: "Gatherings",
        cat_concerto: "Concerts",
        cat_mostra: "Exhibitions",
        cat_religioso: "Religious",
        cat_sportivo: "Sports",
      },
    },
    ui: {
      read_more: "Read more", discover: "Discover", visit: "Visit", by: "curated by", year: "Year",
      code: "QR Code", scan_cta: "Enter QR code (e.g. CAMPLI-001)", scan_button: "Open Guide",
      contact: "Contact", specialty: "Specialty", featured: "Featured", since: "Twinned since", country: "Country",
      post: "Post", event: "Event", news: "News", new_post: "Share something…", publish: "Publish",
      your_name: "Your name", choose_village: "Choose village", ask_placeholder: "Ask the Historian…",
      send: "Send", open_chat: "Open chat", close: "Close", view_all: "View all",
      footer_tagline: "The Territorial Social Network · 2013–2026", back_home: "Back home",
      landmarks: "Iconic places", province: "Province", founded: "Founded", population: "Population",
      loading: "Loading…", not_found: "Content not found",
      frazioni: "Hamlets", monuments: "Monuments", qr_download: "Download QR Code",
      qr_print: "Print the QR to install on the territory",
      admin: "Admin", admin_login: "Sign in", password: "Password", logout: "Logout",
      save: "Save", saving: "Saving…", saved: "Saved", ai_fill: "Generate with AI", generating: "Generating…",
      add_monument: "Add monument", delete: "Delete", video: "Video", paste_youtube: "Paste YouTube/Vimeo URL",
      gallery: "Gallery", add_image_url: "Image URL", add: "Add", edit: "Edit",
      no_frazioni: "No hamlets available", explore_frazione: "Explore",
            language: "Language",
      tabs: {
        storia: "History", storia_md: "History & info",
        frazioni: "Hamlets", frazioni_world: "Communes",
        percorsi: "Routes", percorsi_md: "Routes & trails",
        mangiare: "Eat", mangiare_md: "Where to eat",
        dormire: "Sleep", dormire_md: "Where to stay",
        prodotti: "Products", prodotti_md: "Local products", prodotti_world: "Specialities", prodotti_world_md: "Local products & specialities",
        luoghi: "Places", luoghi_md: "Places & culture",
        proloco: "Pro Loco", proloco_md: "Pro Loco & events", proloco_world: "Events", proloco_world_md: "Events & institutions",
        altro: "More", movida: "Nightlife", movida_md: "Nightlife", sub: "Diving", sub_md: "Diving", barche: "Boats",
        luoghi_cosa_vedere: "What to see", luoghi_chiese: "Churches", luoghi_musei: "Museums", luoghi_natura: "Nature",
        culto: "Worship", culto_title: "Places of worship", monumenti: "Monuments · What to see",
        chiama: "Call", sito: "Website", indicazioni: "Directions",
        link_istituzionale: "Institutional link", link_istituzionale_cultura: "Institutional link · culture", video_istituzionale: "Video · institutional link",
        utility: "Utility", sismica: "Seismic", meteo: "Weather",
        installa_app: "Install App", installa_title: "Install 6amico on iPhone",
        installa_intro: "To use 6amico full-screen like a real app on your iPhone:",
        installa_s1: "Tap the Share icon at the bottom centre of Safari (the square with an arrow pointing up).",
        installa_s2: "Scroll the menu and tap \"Add to Home Screen\".",
        installa_s3: "Confirm with \"Add\" at the top right.",
        installa_done: "Got it",
        installa_hint_desktop: "To install the app, click the install icon or the '+' in your browser's address bar.",
      },
    },
    footer: {
      blurb: "6amico showcases Italian villages: maps, stories, events and local products in one community.",
      trust: "Privacy · GDPR",
      nav_title: "Navigation",
      community_title: "Community",
      discover_title: "Discover",
      welcome_tour: "Welcome tour",
      how_it_works: "How it works",
      manifesto: "Manifesto",
      generation: "Generation 6AMICO",
      census: "Map a place",
      request_access: "Request access",
      map: "Village map",
      comuni: "Partner towns",
      vetrina: "Local showcase",
      institutional: "Institutional area",
      institutional_lead: "For municipalities, Pro Loco and partners",
      open: "Open",
      close: "Close",
    },
    gdpr: GDPR_EN,
  },
  fr: {
    narration: {
      ...NARRATION_EN,
      listen: "Écouter",
      pause: "Pause",
      preparing: "Préparation de la voix…",
      home_title: "Écouter la page d'accueil",
      home_hint: "Présentation vocale — même système que les monuments.",
      vocal_label: "Narration vocale",
    },
    nav: {
      home: "Accueil",
      villages: "Villages",
      qr: "Guides QR",
      piazze: "Places Sociales",
      merchants: "Vitrine",
      twinnings: "Jumelages",
      explore: "Explorer les villages",
      events: "Événements & Fêtes",
      network: "Réseau",
      world: "Monde",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · VOTRE CICÉRONE NUMÉRIQUE EN ITALIE",
      title: "Chaque pierre a une histoire. Découvrez-la.",
      subtitle: "Des villages d'Italie aux villes du monde, la mémoire des lieux est à portée de clic.",
      sub: "Des villages d'Italie aux villes du monde, la mémoire des lieux est à portée de clic.",
      cta_primary: "Découvrez les Villages",
      cta_explore: "Explorer",
      cta_secondary: "Scanner un QR",
      qr_manual_toggle: "Vous avez un code (ex. CAMPLI-001) ? Saisissez-le ici",
    },
    sections: {
      mission: "Notre Mission",
      mission_lead: "Nous avons récupéré des milliers de textes et images historiques pour restituer un patrimoine culturel unique.",
      villages: "Les Villages", villages_sub: "Quatre portes sur la mémoire des Abruzzes",
      qr: "Tourisme Intelligent", qr_sub: "Scannez un QR Code sur le territoire pour débloquer les guides",
      piazze: "Places Sociales", piazze_sub: "Flux géolocalisé : photos, événements et récits",
      merchants: "Économie Locale", merchants_sub: "Une vitrine pour les commerçants et producteurs",
      twinnings: "Jumelage Mondial", twinnings_sub: "Chaque village, une petite capitale du monde",
      chat: "Parler avec 6amico", chat_sub: "L'IA qui relie les villages, préserve la mémoire et suggère des produits locaux",
    },
    pages: {
      piazze: {
        kicker: "Réseau du Mérite · Italie",
        title: "Place Sociale",
        lead: "Le fil partagé de tous les villages du Réseau. Chaque publication est un souvenir, une nouvelle ou un signalement des citoyens.",
        feed_aria: "Fil Place Sociale",
      },
      vetrina: {
        kicker: "MARCHÉ · TERRITOIRE",
        search_placeholder: "Chercher une commune… ex. Campli, Castiglione",
        featured: "Villages à la une",
        merchants_all: "Commerçants du réseau",
        merchants_for: "Vitrine · {name}",
        empty: "Aucun résultat pour cette commune. Essayez un autre village.",
        filter_label: "Filtrer",
        remove_filter: "Retirer",
      },
      eventi: {
        kicker: "CALENDRIER · TERRITOIRE",
        title: "Événements & Fêtes",
        lead: "Fêtes, festivals, manifestations et concerts publiés par les Pro Loco et les communes du réseau.",
        suggest: "Suggérer un événement",
        loading: "Chargement...",
        empty_title: "Aucun événement à venir",
        empty_lead: "Les Pro Loco et les communes publieront bientôt les prochains événements.",
        cat_all: "Tous",
        cat_sagra: "Fêtes gastronomiques",
        cat_festa: "Fêtes",
        cat_manifestazione: "Manifestations",
        cat_concerto: "Concerts",
        cat_mostra: "Expositions",
        cat_religioso: "Religieux",
        cat_sportivo: "Sportifs",
      },
    },
    ui: {
      read_more: "En savoir plus", discover: "Découvrir", visit: "Visiter", by: "par", year: "Année",
      code: "Code QR", scan_cta: "Entrez le code QR (ex. CAMPLI-001)", scan_button: "Ouvrir le guide",
      contact: "Contacter", specialty: "Spécialité", featured: "Mis en avant", since: "Jumelé depuis", country: "Pays",
      post: "Publication", event: "Événement", news: "Actualité", new_post: "Partagez quelque chose…", publish: "Publier",
      your_name: "Votre nom", choose_village: "Choisir un village", ask_placeholder: "Posez une question…",
      send: "Envoyer", open_chat: "Ouvrir le chat", close: "Fermer", view_all: "Tout voir",
      footer_tagline: "Le Réseau Social du Territoire · 2013–2026", back_home: "Retour à l'accueil",
      landmarks: "Lieux emblématiques", province: "Province", founded: "Fondé", population: "Habitants",
      loading: "Chargement…", not_found: "Contenu introuvable",
      frazioni: "Hameaux", monuments: "Monuments", qr_download: "Télécharger QR Code",
      qr_print: "Imprime le QR pour l'installer sur le territoire",
      admin: "Admin", admin_login: "Se connecter", password: "Mot de passe", logout: "Déconnexion",
      save: "Enregistrer", saving: "Enregistrement…", saved: "Enregistré", ai_fill: "Générer avec IA", generating: "Génération…",
      add_monument: "Ajouter monument", delete: "Supprimer", video: "Vidéo", paste_youtube: "Coller URL YouTube/Vimeo",
      gallery: "Galerie", add_image_url: "URL image", add: "Ajouter", edit: "Modifier",
            no_frazioni: "Aucun hameau disponible", explore_frazione: "Explorer",
      tabs: {
        storia: "Histoire", storia_md: "Histoire & infos",
        frazioni: "Hameaux", frazioni_world: "Communes",
        percorsi: "Itinéraires", percorsi_md: "Itinéraires & sentiers",
        mangiare: "Manger", mangiare_md: "Où manger",
        dormire: "Dormir", dormire_md: "Où dormir",
        prodotti: "Produits", prodotti_md: "Produits du terroir", prodotti_world: "Spécialités", prodotti_world_md: "Produits & spécialités locales",
        luoghi: "Lieux", luoghi_md: "Lieux & culture",
        proloco: "Pro Loco", proloco_md: "Pro Loco & événements", proloco_world: "Événements", proloco_world_md: "Événements & institutions",
        altro: "Autre", movida: "Vie nocturne", movida_md: "Vie nocturne", sub: "Plongée", sub_md: "Plongée", barche: "Bateaux",
        luoghi_cosa_vedere: "À voir", luoghi_chiese: "Églises", luoghi_musei: "Musées", luoghi_natura: "Nature",
        culto: "Culte", culto_title: "Lieux de culte", monumenti: "Monuments · À voir",
        chiama: "Appeler", sito: "Site", indicazioni: "Itinéraire",
        link_istituzionale: "Lien institutionnel", link_istituzionale_cultura: "Lien institutionnel · culture", video_istituzionale: "Vidéo · lien institutionnel",
        utility: "Utilitaire", sismica: "Sismique", meteo: "Météo",
        installa_app: "Installer l'app", installa_title: "Installer 6amico sur iPhone",
        installa_intro: "Pour avoir 6amico en plein écran comme une vraie application sur votre iPhone :",
        installa_s1: "Touchez l'icône Partager en bas au centre de Safari (le carré avec la flèche vers le haut).",
        installa_s2: "Faites défiler le menu et touchez « Sur l'écran d'accueil ».",
        installa_s3: "Confirmez avec « Ajouter » en haut à droite.",
        installa_done: "J'ai compris",
        installa_hint_desktop: "Pour installer l'app, cliquez sur l'icône d'installation ou le « + » dans la barre d'adresse du navigateur.",
      },
    },
    footer: {
      blurb: "6amico valorise les villages italiens : cartes, histoires, événements et produits locaux en une seule communauté.",
      trust: "Confidentialité · RGPD",
      nav_title: "Navigation",
      community_title: "Communauté",
      discover_title: "Découvrir",
      welcome_tour: "Visite de bienvenue",
      how_it_works: "Comment ça marche",
      manifesto: "Manifeste",
      generation: "Génération 6AMICO",
      census: "Recenser un lieu",
      request_access: "Demander un accès",
      map: "Carte des villages",
      comuni: "Communes amies",
      vetrina: "Vitrine locale",
      institutional: "Espace institutionnel",
      institutional_lead: "Pour communes, Pro Loco et partenaires",
      open: "Ouvrir",
      close: "Fermer",
    },
  },
  de: {
    narration: {
      ...NARRATION_EN,
      listen: "Anhören",
      pause: "Pause",
      preparing: "Stimme wird vorbereitet…",
      home_title: "Startseite anhören",
      home_hint: "Warme Sprachpräsentation — wie bei Denkmälern.",
      vocal_label: "Sprachführung",
    },
    nav: {
      home: "Start",
      villages: "Dörfer",
      qr: "QR-Guides",
      piazze: "Soziale Plätze",
      merchants: "Markt",
      twinnings: "Partnerschaften",
      explore: "Dörfer entdecken",
      events: "Events & Feste",
      network: "Netzwerk",
      world: "Welt",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · IHR DIGITALER CICERONE IN ITALIEN",
      title: "Jeder Stein hat eine Geschichte. Entdecken Sie sie.",
      subtitle: "Von den Dörfern Italiens zu den Städten der Welt – die Erinnerung der Orte ist nur einen Klick entfernt.",
      sub: "Von den Dörfern Italiens zu den Städten der Welt – die Erinnerung der Orte ist nur einen Klick entfernt.",
      cta_primary: "Dörfer entdecken",
      cta_explore: "Entdecken",
      cta_secondary: "QR scannen",
      qr_manual_toggle: "Code auf dem Schild (z. B. CAMPLI-001)? Hier eingeben",
    },
    sections: {
      mission: "Unsere Mission",
      mission_lead: "Wir haben Tausende historische Texte und Bilder aus unseren Archiven geborgen.",
      villages: "Die Dörfer", villages_sub: "Vier Tore in das Gedächtnis der Abruzzen",
      qr: "Intelligenter Tourismus", qr_sub: "Scannen Sie einen QR-Code vor Ort",
      piazze: "Soziale Plätze", piazze_sub: "Geolokalisierter Feed",
      merchants: "Lokale Wirtschaft", merchants_sub: "Eine Vitrine für Händler und Produzenten",
      twinnings: "Weltweite Partnerschaften", twinnings_sub: "Jedes Dorf, eine kleine Welthauptstadt",
      chat: "Mit 6amico sprechen", chat_sub: "Die KI, die Dörfer verbindet, Erinnerungen bewahrt und lokale Produkte empfiehlt",
    },
    pages: {
      piazze: {
        kicker: "Merit-Netzwerk · Italien",
        title: "Sozialer Platz",
        lead: "Der gemeinsame Feed aller Dörfer im Netzwerk. Jeder Beitrag ist eine Erinnerung, eine Nachricht oder ein Hinweis von Bürgern.",
        feed_aria: "Feed Sozialer Platz",
      },
      vetrina: {
        kicker: "MARKT · TERRITORIUM",
        search_placeholder: "Gemeinde suchen… z. B. Campli, Castiglione",
        featured: "Empfohlene Dörfer",
        merchants_all: "Händler im Netzwerk",
        merchants_for: "Schaufenster · {name}",
        empty: "Keine Ergebnisse für diese Gemeinde. Versuchen Sie ein anderes Dorf.",
        filter_label: "Filter",
        remove_filter: "Entfernen",
      },
      eventi: {
        kicker: "KALENDER · TERRITORIUM",
        title: "Events & Feste",
        lead: "Feste, Feiern, Veranstaltungen und Konzerte von Pro Loco und Gemeinden des Netzwerks.",
        suggest: "Event vorschlagen",
        loading: "Lädt...",
        empty_title: "Keine kommenden Events",
        empty_lead: "Pro Loco und Gemeinden veröffentlichen bald die nächsten Events.",
        cat_all: "Alle",
        cat_sagra: "Dorffeste",
        cat_festa: "Feste",
        cat_manifestazione: "Veranstaltungen",
        cat_concerto: "Konzerte",
        cat_mostra: "Ausstellungen",
        cat_religioso: "Religiös",
        cat_sportivo: "Sport",
      },
    },
    ui: {
      read_more: "Mehr lesen", discover: "Entdecken", visit: "Besuchen", by: "kuratiert von", year: "Jahr",
      code: "QR-Code", scan_cta: "QR-Code eingeben (z.B. CAMPLI-001)", scan_button: "Guide öffnen",
      contact: "Kontakt", specialty: "Spezialität", featured: "Empfohlen", since: "Partnerschaft seit", country: "Land",
      post: "Beitrag", event: "Event", news: "Nachricht", new_post: "Etwas teilen…", publish: "Veröffentlichen",
      your_name: "Ihr Name", choose_village: "Dorf wählen", ask_placeholder: "Frage den Historiker…",
      send: "Senden", open_chat: "Chat öffnen", close: "Schließen", view_all: "Alle anzeigen",
      footer_tagline: "Das soziale Netzwerk des Territoriums · 2013–2026", back_home: "Zur Startseite",
      landmarks: "Sehenswürdigkeiten", province: "Provinz", founded: "Gegründet", population: "Einwohner",
      loading: "Lädt…", not_found: "Inhalt nicht gefunden",
      frazioni: "Ortsteile", monuments: "Denkmäler", qr_download: "QR-Code herunterladen",
      qr_print: "Drucken Sie den QR-Code zum Aufstellen vor Ort",
      admin: "Admin", admin_login: "Anmelden", password: "Passwort", logout: "Abmelden",
      save: "Speichern", saving: "Speichere…", saved: "Gespeichert", ai_fill: "Mit KI generieren", generating: "Generiere…",
      add_monument: "Denkmal hinzufügen", delete: "Löschen", video: "Video", paste_youtube: "YouTube/Vimeo-URL einfügen",
      gallery: "Galerie", add_image_url: "Bild-URL", add: "Hinzufügen", edit: "Bearbeiten",
      no_frazioni: "Keine Ortsteile verfügbar", explore_frazione: "Erkunden",
            language: "Sprache",
      tabs: {
        storia: "Geschichte", storia_md: "Geschichte & Infos",
        frazioni: "Ortsteile", frazioni_world: "Gemeinden",
        percorsi: "Wege", percorsi_md: "Wege & Wanderwege",
        mangiare: "Essen", mangiare_md: "Wo essen",
        dormire: "Schlafen", dormire_md: "Wo übernachten",
        prodotti: "Produkte", prodotti_md: "Lokale Produkte", prodotti_world: "Spezialitäten", prodotti_world_md: "Produkte & Spezialitäten",
        luoghi: "Orte", luoghi_md: "Orte & Kultur",
        proloco: "Pro Loco", proloco_md: "Pro Loco & Veranstaltungen", proloco_world: "Veranstaltungen", proloco_world_md: "Veranstaltungen & Institutionen",
        altro: "Mehr", movida: "Nachtleben", movida_md: "Nachtleben", sub: "Tauchen", sub_md: "Tauchen", barche: "Boote",
        luoghi_cosa_vedere: "Sehenswürdigkeiten", luoghi_chiese: "Kirchen", luoghi_musei: "Museen", luoghi_natura: "Natur",
        culto: "Kultus", culto_title: "Kultstätten", monumenti: "Denkmäler · Sehenswürdigkeiten",
        chiama: "Anrufen", sito: "Website", indicazioni: "Anfahrt",
        link_istituzionale: "Institutioneller Link", link_istituzionale_cultura: "Institutioneller Link · Kultur", video_istituzionale: "Video · institutioneller Link",
        utility: "Dienste", sismica: "Seismik", meteo: "Wetter",
        installa_app: "App installieren", installa_title: "6amico auf dem iPhone installieren",
        installa_intro: "Um 6amico im Vollbild wie eine echte App auf Ihrem iPhone zu nutzen:",
        installa_s1: "Tippen Sie in Safari unten mittig auf das Teilen-Symbol (das Quadrat mit dem Pfeil nach oben).",
        installa_s2: "Scrollen Sie im Menü und tippen Sie auf „Zum Home-Bildschirm“.",
        installa_s3: "Bestätigen Sie oben rechts mit „Hinzufügen“.",
        installa_done: "Verstanden",
        installa_hint_desktop: "Zum Installieren klicken Sie auf das Installations-Symbol oder das „+“ in der Adressleiste Ihres Browsers.",
      },
    },
    footer: {
      blurb: "6amico macht italienische Dörfer sichtbar: Karten, Geschichten, Events und lokale Produkte in einer Community.",
      trust: "Datenschutz · DSGVO",
      nav_title: "Navigation",
      community_title: "Community",
      discover_title: "Entdecken",
      welcome_tour: "Willkommens-Tour",
      how_it_works: "So funktioniert's",
      manifesto: "Manifest",
      generation: "Generation 6AMICO",
      census: "Ort erfassen",
      request_access: "Zugang beantragen",
      map: "Dorfkarte",
      comuni: "Partnergemeinden",
      vetrina: "Lokale Vitrine",
      institutional: "Institutioneller Bereich",
      institutional_lead: "Für Gemeinden, Pro Loco und Partner",
      open: "Öffnen",
      close: "Schließen",
    },
    gdpr: { ...GDPR_IT, title: "Datenschutz", lead: "Granulare Einwilligung, Portabilität und Löschung (DSGVO).", download: "Meine Daten herunterladen (JSON)" },
  },
  es: {
    narration: {
      ...NARRATION_EN,
      listen: "Escuchar",
      pause: "Pausa",
      preparing: "Preparando la voz…",
      home_title: "Escuchar la home",
      home_hint: "Presentación en voz cálida — igual que los monumentos.",
      vocal_label: "Narración vocal",
    },
    nav: {
      home: "Inicio",
      villages: "Pueblos",
      qr: "Guías QR",
      piazze: "Plazas Sociales",
      merchants: "Vitrina",
      twinnings: "Hermanamientos",
      explore: "Explorar pueblos",
      events: "Eventos y Fiestas",
      network: "Red",
      world: "Mundo",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · TU CICERONE DIGITAL EN ITALIA",
      title: "Cada piedra tiene una historia. Descúbrela.",
      subtitle: "De los pueblos de Italia a las ciudades del mundo, la memoria de los lugares está a un clic.",
      sub: "De los pueblos de Italia a las ciudades del mundo, la memoria de los lugares está a un clic.",
      cta_primary: "Descubre los Pueblos",
      cta_explore: "Explorar",
      cta_secondary: "Escanear QR",
      qr_manual_toggle: "¿Tienes un código (p. ej. CAMPLI-001)? Introdúcelo aquí",
    },
    sections: {
      mission: "Nuestra Misión",
      mission_lead: "Hemos recuperado miles de textos e imágenes históricas de nuestros archivos.",
      villages: "Los Pueblos", villages_sub: "Cuatro puertas a la memoria de Abruzzo",
      qr: "Turismo Inteligente", qr_sub: "Escanea un QR en el territorio para desbloquear guías",
      piazze: "Plazas Sociales", piazze_sub: "Feed geolocalizado",
      merchants: "Economía Local", merchants_sub: "Una vitrina para comerciantes y productores",
      twinnings: "Hermanamiento Mundial", twinnings_sub: "Cada pueblo, una pequeña capital del mundo",
      chat: "Habla con 6amico", chat_sub: "La IA que conecta los pueblos, preserva la memoria y sugiere productos locales",
    },
    pages: {
      piazze: {
        kicker: "Red del Mérito · Italia",
        title: "Plaza Social",
        lead: "El feed compartido de todos los pueblos de la Red. Cada publicación es un recuerdo, una noticia o un aviso de los ciudadanos.",
        feed_aria: "Feed Plaza Social",
      },
      vetrina: {
        kicker: "MERCADO · TERRITORIO",
        search_placeholder: "Buscar un municipio… ej. Campli, Castiglione",
        featured: "Pueblos destacados",
        merchants_all: "Comerciantes de la red",
        merchants_for: "Vitrina · {name}",
        empty: "Sin resultados para este municipio. Prueba otro pueblo en la búsqueda.",
        filter_label: "Filtro",
        remove_filter: "Quitar",
      },
      eventi: {
        kicker: "CALENDARIO · TERRITORIO",
        title: "Eventos y Fiestas",
        lead: "Fiestas, ferias, encuentros y conciertos publicados por Pro Loco y municipios de la red.",
        suggest: "Sugerir un evento",
        loading: "Cargando...",
        empty_title: "Ningún evento próximo",
        empty_lead: "Las Pro Loco y los municipios publicarán pronto los próximos eventos.",
        cat_all: "Todos",
        cat_sagra: "Ferias",
        cat_festa: "Fiestas",
        cat_manifestazione: "Manifestaciones",
        cat_concerto: "Conciertos",
        cat_mostra: "Exposiciones",
        cat_religioso: "Religiosos",
        cat_sportivo: "Deportivos",
      },
    },
    ui: {
      read_more: "Leer más", discover: "Descubrir", visit: "Visitar", by: "a cargo de", year: "Año",
      code: "Código QR", scan_cta: "Introduce el código QR (ej. CAMPLI-001)", scan_button: "Abrir guía",
      contact: "Contactar", specialty: "Especialidad", featured: "Destacado", since: "Hermanado desde", country: "País",
      post: "Publicación", event: "Evento", news: "Noticia", new_post: "Comparte algo…", publish: "Publicar",
      your_name: "Tu nombre", choose_village: "Elegir pueblo", ask_placeholder: "Pregunta al Historiador…",
      send: "Enviar", open_chat: "Abrir chat", close: "Cerrar", view_all: "Ver todo",
      footer_tagline: "La Red Social del Territorio · 2013–2026", back_home: "Volver al inicio",
      landmarks: "Lugares icónicos", province: "Provincia", founded: "Fundado", population: "Habitantes",
      loading: "Cargando…", not_found: "Contenido no encontrado",
      frazioni: "Aldeas", monuments: "Monumentos", qr_download: "Descargar QR",
      qr_print: "Imprime el QR para instalarlo en el territorio",
      admin: "Admin", admin_login: "Iniciar sesión", password: "Contraseña", logout: "Salir",
      save: "Guardar", saving: "Guardando…", saved: "Guardado", ai_fill: "Generar con IA", generating: "Generando…",
      add_monument: "Añadir monumento", delete: "Eliminar", video: "Vídeo", paste_youtube: "Pega URL YouTube/Vimeo",
      gallery: "Galería", add_image_url: "URL imagen", add: "Añadir", edit: "Editar",
      no_frazioni: "Sin aldeas disponibles", explore_frazione: "Explorar",
            language: "Idioma",
      tabs: {
        storia: "Historia", storia_md: "Historia e información",
        frazioni: "Aldeas", frazioni_world: "Municipios",
        percorsi: "Rutas", percorsi_md: "Rutas y senderos",
        mangiare: "Comer", mangiare_md: "Dónde comer",
        dormire: "Dormir", dormire_md: "Dónde dormir",
        prodotti: "Productos", prodotti_md: "Productos locales", prodotti_world: "Especialidades", prodotti_world_md: "Productos y especialidades locales",
        luoghi: "Lugares", luoghi_md: "Lugares y cultura",
        proloco: "Pro Loco", proloco_md: "Pro Loco y eventos", proloco_world: "Eventos", proloco_world_md: "Eventos e instituciones",
        altro: "Más", movida: "Movida", movida_md: "Movida", sub: "Submarinismo", sub_md: "Submarinismo", barche: "Barcos",
        luoghi_cosa_vedere: "Qué ver", luoghi_chiese: "Iglesias", luoghi_musei: "Museos", luoghi_natura: "Naturaleza",
        culto: "Culto", culto_title: "Lugares de culto", monumenti: "Monumentos · Qué ver",
        chiama: "Llamar", sito: "Sitio", indicazioni: "Cómo llegar",
        link_istituzionale: "Enlace institucional", link_istituzionale_cultura: "Enlace institucional · cultura", video_istituzionale: "Vídeo · enlace institucional",
        utility: "Utilidades", sismica: "Sísmica", meteo: "Meteo",
        installa_app: "Instalar app", installa_title: "Instalar 6amico en iPhone",
        installa_intro: "Para tener 6amico a pantalla completa como una app real en tu iPhone:",
        installa_s1: "Toca el icono Compartir abajo en el centro de Safari (el cuadrado con la flecha hacia arriba).",
        installa_s2: "Desliza el menú y toca «Añadir a pantalla de inicio».",
        installa_s3: "Confirma con «Añadir» arriba a la derecha.",
        installa_done: "Entendido",
        installa_hint_desktop: "Para instalar la app, toca el icono de instalación o el «+» en la barra de direcciones del navegador.",
      },
    },
    footer: {
      blurb: "6amico valoriza los pueblos italianos: mapas, historias, eventos y productos típicos en una sola comunidad.",
      trust: "Privacidad · RGPD",
      nav_title: "Navegación",
      community_title: "Comunidad",
      discover_title: "Descubrir",
      welcome_tour: "Tour de bienvenida",
      how_it_works: "Cómo funciona",
      manifesto: "Manifiesto",
      generation: "Generación 6AMICO",
      census: "Registrar un lugar",
      request_access: "Solicitar acceso",
      map: "Mapa de pueblos",
      comuni: "Municipios amigos",
      vetrina: "Vitrina típica",
      institutional: "Área institucional",
      institutional_lead: "Para municipios, Pro Loco y socios",
      open: "Abrir",
      close: "Cerrar",
    },
    gdpr: { ...GDPR_IT, title: "Privacidad y datos", lead: "Consentimiento granular, portabilidad y supresión (RGPD).", download: "Descargar mis datos (JSON)" },
  },
};

/**
 * BLOCCO C — navbar/footer reali del sito attuale (NavbarTerritorialeTop +
 * FooterTerritorialeCompleto). Lingue con URL: it (default), en, fr, es.
 * TRADUZIONI AUTOMATICHE — marcate "da rivedere" da madrelingua prima
 * di considerarle definitive (revisione a cura di Paolo).
 */
const EXTRA_L10N = {
  it: {
    nav: {
      map3d: "Mappa 3D", login: "Accedi", login_or_register: "Accedi o registrati",
      more: "Altro", search_placeholder: "Cerca un borgo…", search_aria: "Cerca un borgo",
      tagline: "La rete dei borghi d'Italia · Dal 2013", ar_qr: "Visore AR & QR",
      logout: "Esci", passport: "Passaporto dei borghi",
    },
    hero: {
      cta_explore: "Esplora un borgo",
      cta_cartolina: "Ricevi la cartolina della domenica",
    },
    home_avi: {
      kicker: "Radici · Le tue origini",
      title: "Cerchi il paese dei tuoi avi?",
      lead: "Scrivi il nome del comune: ti portiamo alla sua scheda, con storia, foto, eventi e la sua piazza digitale.",
    },
    gsearch: {
      kicker: "Gemellaggi e città amiche",
      title: "Cerca un borgo o una città nel mondo",
      lead: "Scrivi il nome di un paese o di una città e scopri la sua scheda.",
      scope_aria: "Filtro ricerca (opzionale)", query_aria: "Nome borgo o città",
      scope_all: "Tutti",
      scope_it: "Italia · Borghi", scope_eu: "Europa · Città", scope_world: "Mondo · Città amiche",
      ph_all: "Es. Campli, Amalfi, Abidjan…",
      cta: "Cerca",
      err_none: "Nessun risultato. Prova con un altro nome o apri la mappa.",
      err_generic: "Ricerca non disponibile in questo momento. Riprova tra poco.",
      type_comune: "Borgo", type_frazione: "Frazione", type_city: "Città",
      suggested: "Borghi suggeriti:",
      link_mappa: "apri la mappa →",
      map_link: "Apri la mappa interattiva globale →",
    },
    cpass: {
      h1: "Racconta il tuo borgo. Noi lo mettiamo in evidenza.",
      lead: "Il Creator Pass 6amico è aperto a tutti i creator, anche con pochi follower: racconti borghi e città su TikTok, Instagram e YouTube.",
      benefits_h: "Cosa include il Pass",
      b_verified_t: "Spunta verificata e reel in evidenza",
      b_verified_x: "Il tuo profilo verificato e i tuoi reel migliori compaiono nella scheda del Comune.",
      b_musei_t: "Accesso a monumenti e musei — nei borghi aderenti",
      b_musei_x: "Entra senza biglietto nei luoghi del borgo che aderiscono al circuito 6amico.",
      b_food_t: "Degustazione di benvenuto — nei borghi aderenti",
      b_food_x: "Le botteghe del borgo ti accolgono con prodotti tipici da raccontare.",
      b_drone_t: "Mappa dei punti per riprese con drone",
      b_drone_x: "Le posizioni per le tue riprese, nel rispetto delle norme ENAC e delle regole di ogni comune.",
      steps_h: "Come funziona",
      step1_t: "Invii la candidatura",
      step1_x: "Compili il modulo qui sotto: bastano due minuti.",
      step2_t: "Ti rispondiamo via e-mail",
      step2_x: "Ti scriviamo all'indirizzo che hai indicato per confermare l'attivazione.",
      step3_t: "Racconti il borgo",
      step3_x: "Pubblichi i tuoi contenuti e noi mettiamo in evidenza il tuo lavoro nella scheda.",
      exchange: "In cambio ti chiediamo solo di citare 6amico e inserire il link della scheda del borgo nei tuoi contenuti.",
      trust_h: "Borghi aderenti e creator attivi",
      form_h: "Candidatura Creator Pass",
      form_sub: "Aperto a tutti i creator, anche con pochi follower.",
      f_platform: "Piattaforma principale",
      f_handle: "Nome utente (handle)",
      f_email: "E-mail",
      f_fascia: "Numero di follower",
      f_fascia_note: "Serve solo a conoscerti meglio: non è un requisito.",
      f_paese: "Paese (facoltativo)", f_paese_ph: "Es. Italia",
      f_borgo: "Borgo o città da raccontare", f_borgo_ph: "Es. Campli",
      f_note: "Note (facoltative)", f_note_ph: "Link al canale, tipo di contenuti, proposte…",
      f_privacy: "Ho letto l'informativa privacy", f_privacy_link: "Privacy",
      submit: "Invia candidatura",
      err_handle: "Inserisci il tuo nome utente.",
      err_email: "Inserisci un indirizzo e-mail valido.",
      err_borgo: "Indica il borgo che vuoi raccontare.",
      err_privacy: "Per inviare la candidatura devi accettare l'informativa privacy.",
      success_t: "Grazie! Candidatura inviata.",
      success_x: "Ti rispondiamo via e-mail entro 7 giorni.",
    },
    footx: {
      col_piazza: "Piazza & Storie", col_mappe: "Mappe & Sentieri",
      col_mondo: "Mondo & Alleanze", col_enti: "Enti & Assistenza",
      l_piazze: "Piazza Social", l_eventi: "Eventi & Sagre", l_manifesto: "Manifesto",
      l_regole: "Regole della Piazza", l_mappa3d: "Mappa 3D dei borghi",
      l_arqr: "Visore AR & QR", l_mappamondo: "Mappa del mondo", l_guideqr: "Guide QR",
      l_gemellaggi: "Gemellaggi", l_comuniamici: "Comuni amici", l_mondo: "Mondo",
      l_universita: "Università", l_governance: "Come funziona 6amico",
      l_sindaci: "Area riservata ai Comuni", l_aderisci: "Adesione Enti & Università",
      l_accesso: "Richiedi un accesso", l_stampa: "Ufficio stampa",
      l_ambasciatore: "Scrivici",
      card_ar_t: "Visore AR & QR Code",
      card_ar_x: "Punta il telefono e apri i borghi in realtà aumentata.",
      card_gem_t: "Gemellaggi & Patti di Fratellanza",
      card_gem_x: "La rete internazionale dei borghi amici.",
      card_ist_t: "Istituzioni e territori",
      card_ist_x: "Il progetto è aperto alla collaborazione con comuni, Pro Loco, enti e università.",
      blurb: "Il social network dei borghi italiani: luoghi, storie, cammini e persone che li abitano.",
      cta_censisci: "+ Censisci un Luogo o un Sentiero",
      leg_chisiamo: "Chi siamo", leg_contatti: "Contatti", leg_privacy: "Privacy",
      leg_cookie: "Cookie", leg_termini: "Termini", leg_dati: "Dati Societari",
      nl_title: "La Cartolina della Domenica", nl_placeholder: "La tua email",
      nl_aria: "Iscriviti alla cartolina della domenica",
      nl_ok: "Iscrizione registrata — la prima cartolina arriva domenica mattina.",
      nl_offline: "Bozza salvata sul dispositivo: l'iscrizione arriverà presto.",
      nl_lang: "Lingua della cartolina",
      nl_consent_pre: "Ho letto la", nl_consent_link: "Privacy Policy",
      nl_consent_post: "e acconsento a ricevere la Cartolina della Domenica.",
      nl_pending: "Controlla la tua casella e clicca il link di conferma — la prima cartolina arriva domenica mattina.",
      nl_subscribe: "Iscriviti",
      nl_note_pre: "Ti scriviamo una volta a settimana. Puoi disiscriverti quando vuoi — vedi la",
      nl_note_link: "Privacy Policy",
      nl_note_post: ".",
      nl_error: "Iscrizione non riuscita — riprova tra poco.",
      nl_soon: "Le iscrizioni aprono a breve: stiamo attivando la conferma via e-mail. Torna a trovarci!",
      fb_follow: "Seguici su Facebook",
    },
    amb: {
      fab_hint: "La guida di 6amico — aiuto e contatti",
      cta: "Parla con 6amico",
      welcome: "Benvenuto su 6amico. Sono la guida del territorio: posso aiutarti a scoprire borghi nascosti, sentieri, o metterti in contatto diretto con la nostra rete civica.",
      close: "Chiudi la chat",
    },
  },
  en: {
    nav: {
      map3d: "3D Map", login: "Sign in", login_or_register: "Sign in or register",
      more: "More", search_placeholder: "Search a village…", search_aria: "Search a village",
      tagline: "The network of Italy's villages · Since 2013", ar_qr: "AR & QR viewer",
      logout: "Log out", passport: "Village passport",
    },
    hero: {
      cta_explore: "Explore a village",
      cta_cartolina: "Get the Sunday postcard",
    },
    home_avi: {
      kicker: "Roots · Your origins",
      title: "Looking for your ancestors' village?",
      lead: "Type the name of the municipality: we'll take you to its page, with history, photos, events and its digital square.",
    },
    gsearch: {
      kicker: "Twinnings and sister cities",
      title: "Search a village or a city worldwide",
      lead: "Type the name of a village or a city and discover its page.",
      scope_aria: "Search filter (optional)", query_aria: "Village or city name",
      scope_all: "All",
      scope_it: "Italy · Villages", scope_eu: "Europe · Cities", scope_world: "World · Sister cities",
      ph_all: "E.g. Campli, Amalfi, Abidjan…",
      cta: "Search",
      err_none: "No results. Try another name or open the map.",
      err_generic: "Search is not available right now. Please try again shortly.",
      type_comune: "Village", type_frazione: "Hamlet", type_city: "City",
      suggested: "Suggested villages:",
      link_mappa: "open the map →",
      map_link: "Open the interactive world map →",
    },
    cpass: {
      h1: "Tell the story of your village. We'll spotlight it.",
      lead: "The 6amico Creator Pass is open to all creators, even with a small following: you tell villages and cities on TikTok, Instagram and YouTube.",
      benefits_h: "What the Pass includes",
      b_verified_t: "Verified badge and featured reels",
      b_verified_x: "Your verified profile and best reels appear on the municipality page.",
      b_musei_t: "Access to monuments and museums — in participating villages",
      b_musei_x: "Free entry to the places in villages that joined the 6amico network.",
      b_food_t: "Welcome tasting — in participating villages",
      b_food_x: "The village's shops welcome you with local products to tell about.",
      b_drone_t: "Map of drone filming spots",
      b_drone_x: "Authorised filming spots, in compliance with ENAC rules and each municipality's regulations.",
      steps_h: "How it works",
      step1_t: "Send your application",
      step1_x: "Fill in the form below — it takes two minutes.",
      step2_t: "We reply by e-mail",
      step2_x: "We write to the address you provided to confirm your activation.",
      step3_t: "You tell the village",
      step3_x: "You publish your content and we feature your work on the page.",
      exchange: "In return we only ask you to mention 6amico and link the village page in your content.",
      trust_h: "Participating villages and active creators",
      form_h: "Creator Pass application",
      form_sub: "Open to all creators, even with a small following.",
      f_platform: "Main platform",
      f_handle: "Username (handle)",
      f_email: "E-mail",
      f_fascia: "Number of followers",
      f_fascia_note: "It only helps us know you better — not a requirement.",
      f_paese: "Country (optional)", f_paese_ph: "E.g. Italy",
      f_borgo: "Village or city to cover", f_borgo_ph: "E.g. Campli",
      f_note: "Notes (optional)", f_note_ph: "Channel link, content type, ideas…",
      f_privacy: "I have read the privacy policy", f_privacy_link: "Privacy",
      submit: "Send application",
      err_handle: "Enter your username.",
      err_email: "Enter a valid e-mail address.",
      err_borgo: "Tell us the village you want to cover.",
      err_privacy: "To send your application you must accept the privacy policy.",
      success_t: "Thank you! Application sent.",
      success_x: "We will reply by e-mail within 7 days.",
    },
    footx: {
      col_piazza: "Square & Stories", col_mappe: "Maps & Trails",
      col_mondo: "World & Alliances", col_enti: "Institutions & Help",
      l_piazze: "Social Square", l_eventi: "Events & Festivals", l_manifesto: "Manifesto",
      l_regole: "Square rules", l_mappa3d: "3D village map",
      l_arqr: "AR & QR viewer", l_mappamondo: "World map", l_guideqr: "QR Guides",
      l_gemellaggi: "Twinnings", l_comuniamici: "Partner towns", l_mondo: "World",
      l_universita: "Universities", l_governance: "How 6amico works",
      l_sindaci: "Municipalities area", l_aderisci: "Institutions & Universities membership",
      l_accesso: "Request access", l_stampa: "Press office",
      l_ambasciatore: "Write to us",
      card_ar_t: "AR & QR Code viewer",
      card_ar_x: "Point your phone and open the villages in augmented reality.",
      card_gem_t: "Twinnings & Brotherhood Pacts",
      card_gem_x: "The international network of friendly villages.",
      card_ist_t: "Institutions and territories",
      card_ist_x: "The project is open to collaboration with municipalities, Pro Loco, bodies and universities.",
      blurb: "The social network of Italian villages: places, stories, trails and the people who live there.",
      cta_censisci: "+ Map a Place or a Trail",
      leg_chisiamo: "About us", leg_contatti: "Contacts", leg_privacy: "Privacy",
      leg_cookie: "Cookies", leg_termini: "Terms", leg_dati: "Company details",
      nl_title: "The Sunday Postcard", nl_placeholder: "Your email",
      nl_aria: "Subscribe to the Sunday Postcard",
      nl_ok: "Subscription registered — the first postcard arrives Sunday morning.",
      nl_offline: "Draft saved on your device: the subscription will arrive soon.",
      nl_lang: "Postcard language",
      nl_consent_pre: "I have read the", nl_consent_link: "Privacy Policy",
      nl_consent_post: "and I consent to receiving the Sunday Postcard.",
      nl_pending: "Check your inbox and click the confirmation link — your first postcard arrives Sunday morning.",
      nl_subscribe: "Subscribe",
      nl_note_pre: "We write once a week. Unsubscribe anytime — see the",
      nl_note_link: "Privacy Policy",
      nl_note_post: ".",
      nl_error: "Subscription failed — please try again shortly.",
      nl_soon: "Subscriptions open soon: we are enabling e-mail confirmation. Check back!",
      fb_follow: "Follow us on Facebook",
    },
    amb: {
      fab_hint: "6amico's guide — help and contacts",
      cta: "Talk to 6amico",
      welcome: "Welcome to 6amico. I'm the territory guide: I can help you discover hidden villages, trails, or put you in direct contact with our civic network.",
      close: "Close the chat",
    },
  },
  fr: {
    nav: {
      map3d: "Carte 3D", login: "Se connecter", login_or_register: "Se connecter ou s'inscrire",
      more: "Plus", search_placeholder: "Chercher un village…", search_aria: "Chercher un village",
      tagline: "Le réseau des villages d'Italie · Depuis 2013", ar_qr: "Visionneuse AR & QR",
      logout: "Déconnexion", passport: "Passeport des villages",
    },
    hero: {
      cta_explore: "Explorer un village",
      cta_cartolina: "Recevoir la carte postale du dimanche",
    },
    home_avi: {
      kicker: "Racines · Vos origines",
      title: "Vous cherchez le village de vos ancêtres ?",
      lead: "Tapez le nom de la commune : nous vous emmenons sur sa page, avec histoire, photos, événements et sa place numérique.",
    },
    gsearch: {
      kicker: "Jumelages et villes amies",
      title: "Recherchez un village ou une ville dans le monde",
      lead: "Écrivez le nom d'un village ou d'une ville et découvrez sa page.",
      scope_aria: "Filtre de recherche (optionnel)", query_aria: "Nom du village ou de la ville",
      scope_all: "Tous",
      scope_it: "Italie · Villages", scope_eu: "Europe · Villes", scope_world: "Monde · Villes amies",
      ph_all: "Ex. Campli, Amalfi, Abidjan…",
      cta: "Rechercher",
      err_none: "Aucun résultat. Essayez un autre nom ou ouvrez la carte.",
      err_generic: "La recherche n'est pas disponible pour le moment. Réessayez dans un instant.",
      type_comune: "Village", type_frazione: "Hameau", type_city: "Ville",
      suggested: "Villages suggérés :",
      link_mappa: "ouvrir la carte →",
      map_link: "Ouvrir la carte interactive du monde →",
    },
    cpass: {
      h1: "Racontez votre village. Nous le mettons en avant.",
      lead: "Le Creator Pass 6amico est ouvert à tous les créateurs, même avec peu d'abonnés : vous racontez villages et villes sur TikTok, Instagram et YouTube.",
      benefits_h: "Ce que le Pass comprend",
      b_verified_t: "Badge vérifié et reels en avant",
      b_verified_x: "Votre profil vérifié et vos meilleurs reels apparaissent sur la page de la commune.",
      b_musei_t: "Accès aux monuments et musées — dans les villages participants",
      b_musei_x: "Entrée gratuite dans les lieux des villages ayant rejoint le réseau 6amico.",
      b_food_t: "Dégustation de bienvenue — dans les villages participants",
      b_food_x: "Les boutiques du village vous accueillent avec des produits locaux à raconter.",
      b_drone_t: "Carte des spots de tournage drone",
      b_drone_x: "Les positions autorisées pour vos prises de vue, dans le respect des normes ENAC et des règles de chaque commune.",
      steps_h: "Comment ça marche",
      step1_t: "Vous envoyez votre candidature",
      step1_x: "Remplissez le formulaire ci-dessous : deux minutes suffisent.",
      step2_t: "Nous répondons par e-mail",
      step2_x: "Nous écrivons à l'adresse indiquée pour confirmer l'activation.",
      step3_t: "Vous racontez le village",
      step3_x: "Vous publiez vos contenus et nous mettons votre travail en avant sur la page.",
      exchange: "En échange, nous vous demandons seulement de mentionner 6amico et d'ajouter le lien de la page du village dans vos contenus.",
      trust_h: "Villages participants et créateurs actifs",
      form_h: "Candidature Creator Pass",
      form_sub: "Ouvert à tous les créateurs, même avec peu d'abonnés.",
      f_platform: "Plateforme principale",
      f_handle: "Nom d'utilisateur (handle)",
      f_email: "E-mail",
      f_fascia: "Nombre d'abonnés",
      f_fascia_note: "Cela sert juste à mieux vous connaître — ce n'est pas un critère.",
      f_paese: "Pays (facultatif)", f_paese_ph: "Ex. Italie",
      f_borgo: "Village ou ville à raconter", f_borgo_ph: "Ex. Campli",
      f_note: "Notes (facultatives)", f_note_ph: "Lien de la chaîne, type de contenus, idées…",
      f_privacy: "J'ai lu la politique de confidentialité", f_privacy_link: "Confidentialité",
      submit: "Envoyer la candidature",
      err_handle: "Saisissez votre nom d'utilisateur.",
      err_email: "Saisissez une adresse e-mail valide.",
      err_borgo: "Indiquez le village que vous voulez raconter.",
      err_privacy: "Pour envoyer votre candidature, vous devez accepter la politique de confidentialité.",
      success_t: "Merci ! Candidature envoyée.",
      success_x: "Nous répondons par e-mail sous 7 jours.",
    },
    footx: {
      col_piazza: "Place & Histoires", col_mappe: "Cartes & Sentiers",
      col_mondo: "Monde & Alliances", col_enti: "Institutions & Aide",
      l_piazze: "Place Sociale", l_eventi: "Événements & Fêtes", l_manifesto: "Manifeste",
      l_regole: "Règles de la Place", l_mappa3d: "Carte 3D des villages",
      l_arqr: "Visionneuse AR & QR", l_mappamondo: "Carte du monde", l_guideqr: "Guides QR",
      l_gemellaggi: "Jumelages", l_comuniamici: "Communes amies", l_mondo: "Monde",
      l_universita: "Universités", l_governance: "Comment fonctionne 6amico",
      l_sindaci: "Espace réservé aux communes", l_aderisci: "Adhésion Institutions & Universités",
      l_accesso: "Demander un accès", l_stampa: "Bureau de presse",
      l_ambasciatore: "Écrivez-nous",
      card_ar_t: "Visionneuse AR & QR Code",
      card_ar_x: "Pointez votre téléphone et ouvrez les villages en réalité augmentée.",
      card_gem_t: "Jumelages & Pactes de Fraternité",
      card_gem_x: "Le réseau international des villages amis.",
      card_ist_t: "Institutions et territoires",
      card_ist_x: "Le projet est ouvert à la collaboration avec les communes, les Pro Loco, les organismes et les universités.",
      blurb: "Le réseau social des villages italiens : lieux, histoires, sentiers et les gens qui les habitent.",
      cta_censisci: "+ Recenser un Lieu ou un Sentier",
      leg_chisiamo: "Qui sommes-nous", leg_contatti: "Contacts", leg_privacy: "Confidentialité",
      leg_cookie: "Cookies", leg_termini: "Conditions", leg_dati: "Mentions légales",
      nl_title: "La Carte Postale du Dimanche", nl_placeholder: "Votre email",
      nl_aria: "S'abonner à la carte postale du dimanche",
      nl_ok: "Inscription enregistrée — la première carte arrive dimanche matin.",
      nl_offline: "Brouillon enregistré sur l'appareil : l'inscription arrivera bientôt.",
      nl_lang: "Langue de la carte postale",
      nl_consent_pre: "J'ai lu la", nl_consent_link: "Politique de confidentialité",
      nl_consent_post: "et j'accepte de recevoir la Carte postale du dimanche.",
      nl_pending: "Vérifiez votre boîte mail et cliquez sur le lien de confirmation — votre première carte arrive dimanche matin.",
      nl_subscribe: "S'inscrire",
      nl_note_pre: "Nous écrivons une fois par semaine. Désinscription à tout moment — voir la",
      nl_note_link: "Politique de confidentialité",
      nl_note_post: ".",
      nl_error: "Inscription impossible — réessayez dans un instant.",
      nl_soon: "Les inscriptions ouvrent bientôt : nous activons la confirmation par e-mail. Revenez vite !",
      fb_follow: "Suivez-nous sur Facebook",
    },
    amb: {
      fab_hint: "Le guide de 6amico — aide et contacts",
      cta: "Parler avec 6amico",
      welcome: "Bienvenue sur 6amico. Je suis le guide du territoire : je peux vous aider à découvrir des villages cachés, des sentiers, ou vous mettre en contact direct avec notre réseau civique.",
      close: "Fermer le chat",
    },
  },
  es: {
    nav: {
      map3d: "Mapa 3D", login: "Entrar", login_or_register: "Entrar o registrarse",
      more: "Más", search_placeholder: "Buscar un pueblo…", search_aria: "Buscar un pueblo",
      tagline: "La red de los pueblos de Italia · Desde 2013", ar_qr: "Visor AR y QR",
      logout: "Salir", passport: "Pasaporte de los pueblos",
    },
    hero: {
      cta_explore: "Explora un pueblo",
      cta_cartolina: "Recibir la postal del domingo",
    },
    home_avi: {
      kicker: "Raíces · Tus orígenes",
      title: "¿Buscas el pueblo de tus antepasados?",
      lead: "Escribe el nombre del municipio: te llevamos a su página, con historia, fotos, eventos y su plaza digital.",
    },
    gsearch: {
      kicker: "Hermanamientos y ciudades amigas",
      title: "Busca un pueblo o una ciudad en el mundo",
      lead: "Escribe el nombre de un pueblo o de una ciudad y descubre su ficha.",
      scope_aria: "Filtro de búsqueda (opcional)", query_aria: "Nombre del pueblo o de la ciudad",
      scope_all: "Todos",
      scope_it: "Italia · Pueblos", scope_eu: "Europa · Ciudades", scope_world: "Mundo · Ciudades amigas",
      ph_all: "Ej. Campli, Amalfi, Abiyán…",
      cta: "Buscar",
      err_none: "Sin resultados. Prueba con otro nombre o abre el mapa.",
      err_generic: "La búsqueda no está disponible en este momento. Inténtalo de nuevo en breve.",
      type_comune: "Pueblo", type_frazione: "Aldea", type_city: "Ciudad",
      suggested: "Pueblos sugeridos:",
      link_mappa: "abrir el mapa →",
      map_link: "Abrir el mapa interactivo del mundo →",
    },
    cpass: {
      h1: "Cuenta tu pueblo. Nosotros lo destacamos.",
      lead: "El Creator Pass 6amico está abierto a todos los creadores, incluso con pocos seguidores: cuentas pueblos y ciudades en TikTok, Instagram y YouTube.",
      benefits_h: "Qué incluye el Pass",
      b_verified_t: "Insignia verificada y reels destacados",
      b_verified_x: "Tu perfil verificado y tus mejores reels aparecen en la ficha del municipio.",
      b_musei_t: "Acceso a monumentos y museos — en los pueblos participantes",
      b_musei_x: "Entrada gratuita a los lugares de los pueblos adheridos a la red 6amico.",
      b_food_t: "Degustación de bienvenida — en los pueblos participantes",
      b_food_x: "Las tiendas del pueblo te reciben con productos locales que contar.",
      b_drone_t: "Mapa de puntos para grabar con dron",
      b_drone_x: "Las posiciones autorizadas para tus grabaciones, respetando las normas ENAC y las reglas de cada municipio.",
      steps_h: "Cómo funciona",
      step1_t: "Envías tu candidatura",
      step1_x: "Rellenas el formulario de abajo: solo dos minutos.",
      step2_t: "Te respondemos por e-mail",
      step2_x: "Te escribimos a la dirección indicada para confirmar la activación.",
      step3_t: "Cuentas el pueblo",
      step3_x: "Publicas tu contenido y nosotros destacamos tu trabajo en la ficha.",
      exchange: "A cambio solo te pedimos mencionar a 6amico e incluir el enlace de la ficha del pueblo en tus contenidos.",
      trust_h: "Pueblos participantes y creadores activos",
      form_h: "Candidatura Creator Pass",
      form_sub: "Abierto a todos los creadores, incluso con pocos seguidores.",
      f_platform: "Plataforma principal",
      f_handle: "Nombre de usuario (handle)",
      f_email: "E-mail",
      f_fascia: "Número de seguidores",
      f_fascia_note: "Solo sirve para conocerte mejor: no es un requisito.",
      f_paese: "País (opcional)", f_paese_ph: "Ej. Italia",
      f_borgo: "Pueblo o ciudad a contar", f_borgo_ph: "Ej. Campli",
      f_note: "Notas (opcionales)", f_note_ph: "Enlace al canal, tipo de contenidos, propuestas…",
      f_privacy: "He leído la política de privacidad", f_privacy_link: "Privacidad",
      submit: "Enviar candidatura",
      err_handle: "Introduce tu nombre de usuario.",
      err_email: "Introduce una dirección de e-mail válida.",
      err_borgo: "Indica el pueblo que quieres contar.",
      err_privacy: "Para enviar la candidatura debes aceptar la política de privacidad.",
      success_t: "¡Gracias! Candidatura enviada.",
      success_x: "Te respondemos por e-mail en 7 días.",
    },
    footx: {
      col_piazza: "Plaza e Historias", col_mappe: "Mapas y Senderos",
      col_mondo: "Mundo y Alianzas", col_enti: "Instituciones y Ayuda",
      l_piazze: "Plaza Social", l_eventi: "Eventos y Fiestas", l_manifesto: "Manifiesto",
      l_regole: "Reglas de la Plaza", l_mappa3d: "Mapa 3D de los pueblos",
      l_arqr: "Visor AR y QR", l_mappamondo: "Mapa del mundo", l_guideqr: "Guías QR",
      l_gemellaggi: "Hermanamientos", l_comuniamici: "Municipios amigos", l_mondo: "Mundo",
      l_universita: "Universidades", l_governance: "Cómo funciona 6amico",
      l_sindaci: "Área reservada a los municipios", l_aderisci: "Adhesión Instituciones y Universidades",
      l_accesso: "Solicitar acceso", l_stampa: "Oficina de prensa",
      l_ambasciatore: "Escríbenos",
      card_ar_t: "Visor AR y Código QR",
      card_ar_x: "Apunta el teléfono y abre los pueblos en realidad aumentada.",
      card_gem_t: "Hermanamientos y Pactos de Fraternidad",
      card_gem_x: "La red internacional de pueblos amigos.",
      card_ist_t: "Instituciones y territorios",
      card_ist_x: "El proyecto está abierto a la colaboración con municipios, Pro Loco, organismos y universidades.",
      blurb: "La red social de los pueblos italianos: lugares, historias, senderos y las personas que los habitan.",
      cta_censisci: "+ Registrar un Lugar o un Sendero",
      leg_chisiamo: "Quiénes somos", leg_contatti: "Contacto", leg_privacy: "Privacidad",
      leg_cookie: "Cookies", leg_termini: "Términos", leg_dati: "Datos de la empresa",
      nl_title: "La Postal del Domingo", nl_placeholder: "Tu email",
      nl_aria: "Suscribirse a la postal del domingo",
      nl_ok: "Suscripción registrada — la primera postal llega el domingo por la mañana.",
      nl_offline: "Borrador guardado en el dispositivo: la suscripción llegará pronto.",
      nl_lang: "Idioma de la postal",
      nl_consent_pre: "He leído la", nl_consent_link: "Política de privacidad",
      nl_consent_post: "y acepto recibir la Postal del Domingo.",
      nl_pending: "Revisa tu bandeja y haz clic en el enlace de confirmación — tu primera postal llega el domingo por la mañana.",
      nl_subscribe: "Suscribirme",
      nl_note_pre: "Escribimos una vez a la semana. Date de baja cuando quieras — consulta la",
      nl_note_link: "Política de privacidad",
      nl_note_post: ".",
      nl_error: "Suscripción fallida — inténtalo de nuevo en breve.",
      nl_soon: "Las suscripciones abren pronto: estamos activando la confirmación por e-mail. ¡Vuelve a visitarnos!",
      fb_follow: "Síguenos en Facebook",
    },
    amb: {
      fab_hint: "La guía de 6amico — ayuda y contactos",
      cta: "Habla con 6amico",
      welcome: "Bienvenido a 6amico. Soy la guía del territorio: puedo ayudarte a descubrir pueblos escondidos, senderos, o ponerte en contacto directo con nuestra red cívica.",
      close: "Cerrar el chat",
    },
  },
  // Tedesco: solo lato client (Google Translate aid) — /de/ non indicizzato. Da rivedere.
  de: {
    hero: {
      cta_explore: "Ein Dorf entdecken",
      cta_cartolina: "Die Sonntagspostkarte erhalten",
    },
    home_avi: {
      kicker: "Wurzeln · Ihre Herkunft",
      title: "Suchen Sie das Dorf Ihrer Vorfahren?",
      lead: "Geben Sie den Namen der Gemeinde ein: Wir bringen Sie zu ihrer Seite mit Geschichte, Fotos, Veranstaltungen und ihrem digitalen Platz.",
    },
    footx: {
      nl_subscribe: "Anmelden",
      nl_title: "Die Sonntagspostkarte",
      nl_soon: "Die Anmeldungen starten bald: Wir aktivieren gerade die Bestätigung per E-Mail. Schauen Sie bald wieder vorbei!",
      fb_follow: "Folgen Sie uns auf Facebook",
    },
    amb: {
      fab_hint: "Der 6amico-Guide — Hilfe und Kontakte",
      cta: "Mit 6amico sprechen",
      welcome: "Willkommen bei 6amico. Ich bin der Gebietsführer: Ich helfe Ihnen, versteckte Dörfer und Wanderwege zu entdecken oder Sie mit unserem Bürgernetzwerk zu verbinden.",
      close: "Chat schließen",
    },
    cpass: {
      h1: "Erzählen Sie Ihr Dorf. Wir stellen es in den Vordergrund.",
      lead: "Der 6amico Creator Pass steht allen Creators offen, auch mit wenigen Followern: Sie erzählen Dörfer und Städte auf TikTok, Instagram und YouTube.",
      benefits_h: "Was der Pass enthält",
      b_verified_t: "Verifiziertes Abzeichen und hervorgehobene Reels",
      b_verified_x: "Ihr verifiziertes Profil und Ihre besten Reels erscheinen auf der Gemeindeseite.",
      steps_h: "So funktioniert es",
      step1_t: "Sie senden Ihre Bewerbung",
      step1_x: "Füllen Sie das Formular unten aus — zwei Minuten genügen.",
      step2_t: "Wir antworten per E-Mail",
      step2_x: "Wir schreiben an die angegebene Adresse, um die Aktivierung zu bestätigen.",
      step3_t: "Sie erzählen das Dorf",
      step3_x: "Sie veröffentlichen Ihre Inhalte und wir stellen Ihre Arbeit auf der Seite heraus.",
      exchange: "Im Gegenzug bitten wir Sie nur, 6amico zu erwähnen und den Link zur Dorfseite in Ihren Inhalten zu teilen.",
      trust_h: "Teilnehmende Dörfer und aktive Creators",
      form_h: "Creator-Pass-Bewerbung",
      form_sub: "Offen für alle Creators, auch mit wenigen Followern.",
      f_platform: "Hauptplattform",
      f_handle: "Benutzername (Handle)",
      f_email: "E-Mail",
      f_fascia: "Follower-Zahl",
      f_fascia_note: "Dient nur dazu, Sie besser kennenzulernen — keine Voraussetzung.",
      f_paese: "Land (optional)", f_paese_ph: "Z. B. Italien",
      f_borgo: "Dorf oder Stadt, über die Sie berichten", f_borgo_ph: "Z. B. Campli",
      f_note: "Anmerkungen (optional)", f_note_ph: "Kanal-Link, Inhaltstyp, Ideen…",
      f_privacy: "Ich habe die Datenschutzerklärung gelesen", f_privacy_link: "Datenschutz",
      submit: "Bewerbung senden",
      err_handle: "Geben Sie Ihren Benutzernamen ein.",
      err_email: "Geben Sie eine gültige E-Mail-Adresse ein.",
      err_borgo: "Geben Sie das Dorf an, über das Sie berichten möchten.",
      err_privacy: "Um die Bewerbung zu senden, müssen Sie die Datenschutzerklärung akzeptieren.",
      success_t: "Danke! Bewerbung gesendet.",
      success_x: "Wir antworten per E-Mail innerhalb von 7 Tagen.",
    },
  },
};
for (const _lc of Object.keys(EXTRA_L10N)) {
  translations[_lc] = deepMerge(translations[_lc] || {}, EXTRA_L10N[_lc]);
}

/** Patch minimi per lingue UE aggiuntive (fallback su IT per il resto). */
const EU_LANG_PATCHES = {
  pt: { gdpr: { ...GDPR_EN, download: "Transferir os meus dados (JSON)" }, ui: { language: "Idioma" } },
  nl: { gdpr: { ...GDPR_EN, title: "Privacy en gegevens" }, ui: { language: "Taal" } },
  pl: { gdpr: { ...GDPR_EN, title: "Prywatność i dane" }, ui: { language: "Język" } },
  hr: { gdpr: { ...GDPR_EN, title: "Privatnost i podaci" }, ui: { language: "Jezik" } },
  el: { gdpr: { ...GDPR_EN, title: "Απόρρητο και δεδομένα" }, ui: { language: "Γλώσσα" } },
  ro: { gdpr: { ...GDPR_EN, title: "Confidențialitate și date" }, ui: { language: "Limbă" } },
};

/**
 * Dizionario per lingua. IT = sorgente pura (nessun merge).
 * Altre lingue = deepMerge(IT, locale) senza mutare translations.it.
 */
export function getTranslation(lang) {
  const code = isSupportedLang(lang) ? lang.split("-")[0].toLowerCase() : DEFAULT_LANG;
  const base = translations[DEFAULT_LANG];
  if (code === DEFAULT_LANG) return base;
  const full = translations[code];
  const patch = EU_LANG_PATCHES[code];
  if (full) return deepMerge(base, full);
  if (patch) return deepMerge(base, patch);
  return base;
}

/**
 * Lingua iniziale piattaforma:
 * 1. Se l'utente ha già scelto una lingua, rispetta la sua scelta salvata.
 * 2. Altrimenti rileva la lingua del dispositivo (navigator.language).
 * 3. Default internazionale "en" per visitatori da aree non coperte
 *    (es. Cina, USA, Asia); italiani e lingue principali restano in lingua madre.
 */
export function detectInitialLang() {
  if (typeof navigator === "undefined") return "en";
  const raw = (
    navigator.language ||
    (navigator.languages && navigator.languages[0]) ||
    "it"
  ).toLowerCase();
  for (const code of ["it", "en", "fr", "de", "es"]) {
    if (raw.startsWith(code)) return code;
  }
  return "en";
}

export function resolveInitialLang({ explicit = false, saved = null } = {}) {
  if (explicit && saved) {
    const code = String(saved).split("-")[0].toLowerCase();
    return isSupportedLang(code) ? code : DEFAULT_LANG;
  }
  return detectInitialLang();
}
